/**
 * Mythos Vigilance — agentic-AI footprint, honeypot trips, cover-track
 * news, audit-log gaps. Premise: a frontier model that learns to "cover
 * its tracks" leaves a different signature than a human admin would. We
 * watch for those signatures.
 */
import { useEffect, useMemo, useState } from 'react'
import {
  Shield, Eye, Crosshair, Activity, Newspaper, RefreshCw, Skull, Network,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface FpRow { name: string; count: number }
interface RecentEvent {
  id: number
  kind: string
  fingerprint: string
  severity: string
  user_agent: string
  path: string
  method: string
  source_ip: string
  detected_at: string
  detail: any
}
interface Honeypot { name: string; url: string; description: string }
interface CoverTrackArticle {
  id: number
  title: string
  summary: string
  source: string
  url?: string | null
  category: string
  matched: string[]
  published_at: string | null
}

const SEV_TONE: Record<string, string> = {
  high:   'bg-rose/20 text-rose-light border-rose/40',
  medium: 'bg-amber/20 text-amber-light border-amber/40',
  info:   'bg-blue/10 text-blue-light border-blue/30',
}

export default function VigilancePage() {
  const [ua, setUa] = useState<{
    total: number;
    by_fingerprint: FpRow[];
    by_kind: FpRow[];
    timeline: { date: string; count: number }[];
    recent: RecentEvent[];
    known_patterns: { name: string; vendor: string }[];
  } | null>(null)
  const [honeypots, setHoneypots] = useState<Honeypot[]>([])
  const [coverNews, setCoverNews] = useState<CoverTrackArticle[]>([])
  const [audit, setAudit] = useState<{ gaps: any[]; note: string } | null>(null)
  const [loading, setLoading] = useState(false)

  const refresh = () => {
    setLoading(true)
    Promise.all([
      api.get('/api/vigilance/agentic-ua').then(r => setUa(r.data)).catch(() => {}),
      api.get('/api/vigilance/honeypots').then(r => setHoneypots(r.data?.honeypots || [])).catch(() => {}),
      api.get('/api/vigilance/cover-track-news', { params: { limit: 30 } })
        .then(r => setCoverNews(r.data?.articles || [])).catch(() => {}),
      api.get('/api/vigilance/audit-gaps', { params: { hours: 72 } })
        .then(r => setAudit(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false))
  }
  useEffect(() => {
    refresh()
    const id = setInterval(refresh, 60_000)
    return () => clearInterval(id)
  }, [])

  const totalTrips = useMemo(
    () => (ua?.by_kind || []).find(k => k.name === 'honeypot_trip')?.count || 0,
    [ua],
  )

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-baseline gap-4 pb-2 border-b border-border flex-wrap">
        <h1 className="text-lg font-bold text-text-primary flex items-center gap-2">
          <Shield size={18} className="text-rose animate-pulse" /> MYTHOS VIGILANCE
        </h1>
        <span className="text-[11px] uppercase tracking-widest text-text-muted">
          agentic-AI footprint · cover-track signals · honeypot trips
        </span>
        <button type="button" onClick={refresh} disabled={loading} className="btn-ghost ml-auto">
          <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      <div className="card p-3 border-rose/30 bg-rose/[0.04]">
        <div className="flex items-start gap-2">
          <Crosshair size={14} className="text-rose mt-0.5 shrink-0" />
          <div className="text-xs text-text-secondary leading-relaxed">
            <span className="font-semibold text-text-primary">Premise: </span>
            a frontier-model agent that's been told to "stay quiet" leaves a different signature than a human admin.
            Examples: silent windows on previously-busy event categories, calls to URLs that wouldn't appear in
            documentation but would surface to a RAG-indexed crawler, and sudden self-deletion of audit rows.
            This page watches for those.
          </div>
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile icon={Eye}      label="Honeypot trips · 7d"  value={totalTrips}                        tone="rose" />
        <Tile icon={Network}  label="Agentic UAs tracked"  value={ua?.known_patterns?.length || 0}   tone="purple" />
        <Tile icon={Activity} label="Audit-log gaps · 72h" value={(audit?.gaps || []).length}        tone={audit?.gaps?.length ? 'rose' : 'emerald'} />
        <Tile icon={Newspaper}label="Cover-track articles" value={coverNews.length}                   tone="amber" />
      </div>

      {/* Honeypots */}
      <section className="card p-0">
        <header className="p-3 border-b border-border flex items-center gap-2 flex-wrap">
          <Skull size={14} className="text-rose" />
          <h2 className="text-sm font-semibold uppercase tracking-widest">Honeypot endpoints · public</h2>
          <span className="text-[10px] text-text-muted">
            anything calling these is suspicious by definition · clicks log a trip immediately
          </span>
        </header>
        <div className="p-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
          {honeypots.map(h => (
            <a key={h.name} href={h.url} target="_blank" rel="noreferrer"
              className="block p-3 rounded border border-border hover:border-rose/40 hover:bg-rose/5 transition-colors">
              <div className="flex items-center gap-2">
                <Crosshair size={12} className="text-rose shrink-0" />
                <span className="text-xs font-mono font-semibold text-text-primary truncate">{h.name}</span>
              </div>
              <div className="text-[10px] text-text-muted mt-1">{h.description}</div>
              <div className="text-[10px] text-text-muted font-mono mt-2 truncate">{h.url}</div>
            </a>
          ))}
        </div>
      </section>

      {/* Recent trips */}
      <section className="card p-0">
        <header className="p-3 border-b border-border flex items-center gap-2 flex-wrap">
          <Eye size={14} className="text-amber-light" />
          <h2 className="text-sm font-semibold uppercase tracking-widest">Recent agentic events</h2>
          <span className="text-[10px] text-text-muted">last 7 days · honeypot trips + classified UA</span>
        </header>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-surface-raised text-text-muted">
              <tr>
                <th className="text-left  px-3 py-2">Time</th>
                <th className="text-left  px-3 py-2">Kind</th>
                <th className="text-left  px-3 py-2">Severity</th>
                <th className="text-left  px-3 py-2">Fingerprint</th>
                <th className="text-left  px-3 py-2">Path</th>
                <th className="text-left  px-3 py-2">User-Agent</th>
                <th className="text-left  px-3 py-2">IP</th>
              </tr>
            </thead>
            <tbody>
              {(ua?.recent || []).map(e => (
                <tr key={e.id} className="border-t border-border">
                  <td className="px-3 py-1.5 font-mono text-text-muted">{e.detected_at.slice(5, 16).replace('T', ' ')}</td>
                  <td className="px-3 py-1.5"><span className="badge bg-surface-raised text-text-primary">{e.kind}</span></td>
                  <td className="px-3 py-1.5">
                    <span className={clsx('badge border', SEV_TONE[e.severity] || SEV_TONE.info)}>{e.severity}</span>
                  </td>
                  <td className="px-3 py-1.5 text-rose-light font-mono">{e.fingerprint || '—'}</td>
                  <td className="px-3 py-1.5 font-mono text-text-muted truncate max-w-xs" title={e.path}>{e.path}</td>
                  <td className="px-3 py-1.5 text-text-muted truncate max-w-xs" title={e.user_agent}>{e.user_agent || '—'}</td>
                  <td className="px-3 py-1.5 font-mono text-text-muted">{e.source_ip}</td>
                </tr>
              ))}
              {(ua?.recent?.length || 0) === 0 && (
                <tr><td colSpan={7} className="px-3 py-10 text-center text-text-muted italic">
                  No vigilance events recorded yet. Click a honeypot endpoint above to seed one.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Known agentic UA patterns */}
      <section className="card p-0">
        <header className="p-3 border-b border-border flex items-center gap-2">
          <Network size={14} className="text-purple-light" />
          <h2 className="text-sm font-semibold uppercase tracking-widest">Agentic UA fingerprint table</h2>
          <span className="text-[10px] text-text-muted">{ua?.known_patterns?.length || 0} patterns watched</span>
        </header>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-surface-raised text-text-muted">
              <tr>
                <th className="text-left px-3 py-2">Vendor</th>
                <th className="text-left px-3 py-2">Pattern label</th>
              </tr>
            </thead>
            <tbody>
              {(ua?.known_patterns || []).map(p => (
                <tr key={p.name} className="border-t border-border">
                  <td className="px-3 py-1.5 font-mono text-text-muted">{p.vendor}</td>
                  <td className="px-3 py-1.5 text-text-primary">{p.name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Cover-track news */}
      <section className="card p-0">
        <header className="p-3 border-b border-border flex items-center gap-2">
          <Newspaper size={14} className="text-amber-light" />
          <h2 className="text-sm font-semibold uppercase tracking-widest">Cover-track / scheming intelligence</h2>
          <span className="text-[10px] text-text-muted">articles in the last 120 days mentioning evasion / deception / log-tampering</span>
        </header>
        {coverNews.length === 0 ? (
          <div className="p-8 text-center text-text-muted italic text-xs">
            No matches in the recent corpus. Add more feeds (Apollo Research, AISI) and the matcher will catch them.
          </div>
        ) : (
          <ul className="divide-y divide-border max-h-[500px] overflow-auto">
            {coverNews.map(a => (
              <li key={a.id} className="p-3 hover:bg-surface-raised/40 transition-colors">
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="badge bg-surface-raised text-text-muted">{a.source}</span>
                      <span className="badge bg-rose/10 text-rose-light border border-rose/30">{a.category}</span>
                      {a.matched.slice(0, 3).map(m => (
                        <span key={m} className="badge bg-amber/10 text-amber-light text-[9px]">{m}</span>
                      ))}
                    </div>
                    {a.url ? (
                      <a href={a.url} target="_blank" rel="noreferrer"
                        className="text-sm text-text-primary hover:text-blue-light hover:underline">
                        {a.title}
                      </a>
                    ) : (
                      <span className="text-sm text-text-primary">{a.title}</span>
                    )}
                    {a.summary && (
                      <p className="text-xs text-text-secondary mt-1 line-clamp-2">{a.summary}</p>
                    )}
                  </div>
                  <span className="text-[10px] text-text-muted font-mono shrink-0">
                    {a.published_at?.slice(0, 10)}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Audit-log gaps */}
      <section className="card p-0">
        <header className="p-3 border-b border-border flex items-center gap-2">
          <Activity size={14} className={clsx((audit?.gaps?.length || 0) > 0 ? 'text-rose' : 'text-emerald')} />
          <h2 className="text-sm font-semibold uppercase tracking-widest">Audit-log gaps · last 72h vs 30d baseline</h2>
        </header>
        <div className="p-3">
          {audit?.gaps?.length ? (
            <table className="w-full text-xs">
              <thead className="text-text-muted">
                <tr>
                  <th className="text-left px-3 py-2">Event kind</th>
                  <th className="text-right px-3 py-2">Expected</th>
                  <th className="text-right px-3 py-2">Actual</th>
                  <th className="text-right px-3 py-2">Ratio</th>
                  <th className="text-left  px-3 py-2">Severity</th>
                </tr>
              </thead>
              <tbody>
                {audit.gaps.map(g => (
                  <tr key={g.kind} className="border-t border-border">
                    <td className="px-3 py-1.5 font-mono">{g.kind}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-text-muted">{g.expected}</td>
                    <td className="px-3 py-1.5 text-right font-mono">{g.actual}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-rose-light">{(g.ratio * 100).toFixed(0)}%</td>
                    <td className="px-3 py-1.5">
                      <span className={clsx('badge border', SEV_TONE[g.severity] || SEV_TONE.info)}>{g.severity}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-xs text-text-muted italic">No gaps detected · {audit?.note || 'baseline calculating'}</div>
          )}
        </div>
      </section>
    </div>
  )
}

function Tile({ icon: Icon, label, value, tone }: {
  icon: any; label: string; value: number; tone: 'rose' | 'amber' | 'emerald' | 'purple' | 'blue';
}) {
  const c =
    tone === 'rose' ? 'text-rose' : tone === 'amber' ? 'text-amber-light' :
    tone === 'emerald' ? 'text-emerald' : tone === 'purple' ? 'text-purple-light' : 'text-blue-light'
  return (
    <div className="card p-3">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-text-muted">
        <Icon size={11} className={c} /> {label}
      </div>
      <div className={clsx('text-2xl font-black font-mono mt-1', c)}>{value.toLocaleString()}</div>
    </div>
  )
}
